import ContextStateHelper from 'State/Game/Context/ContextStateHelper';
import { ContextType } from 'State/Game/ContextType';
import GameStateHelper from 'State/Game/GameStateHelper';
import { IGameState } from 'State/Game/IGameState';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { scaleCoinRewardWithLevelCost } from 'State/Player/PlayerStateHelper';
import { PopupID } from 'State/UI/PopupID';
import UIStateHelper from 'State/UI/UIStateHelper';
import { PlinkoReward } from 'System/Plinko/PlinkoReward';
import { Random } from 'Utility/Random';
import { IAPReward } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { initialPlinkoState } from './initialPlinkoState';
import { IPlinkoState } from './IPlinkoState';
import { PlinkoPathValue } from 'Prefabs/Plinko/PlinkoToken';
import { getPlinkoConfig } from 'System/Plinko/Helpers';
import { MathHelper } from 'Utility/MathHelper';
import LevelHelper from 'Utility/LevelHelper';
import { PlayerTask } from 'State/Player/PlayerTasks';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { TActionType, TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { IPlinkoSceneParams } from 'Scene/PlinkoScene';

export const dropPlinkoToken = (
    state: IGameState,
    payload: { startIndex: number; anim: string; rewards: Random.IWeightedItem<PlinkoReward>[] }
): IGameState => {
    // Assign indexes
    const rewardsWithIndexes: Random.IWeightedItem<PlinkoReward>[] = payload.rewards.map((reward, index) => ({
        ...reward,
        data: { ...reward.data, index },
    }));

    // Assuming 2 drop zones rewards should exclude either the first or last reward.
    const reachableRewards = payload.startIndex === 0 ? rewardsWithIndexes.slice(0, -1) : rewardsWithIndexes.slice(1);

    // Calculate reward using weights or is jackpot is to be forced then find the index of the first reward with the jackpot property === true.
    const rewardIndex = (state.activeScene?.params as IPlinkoSceneParams)?.forceJackpot
        ? reachableRewards.findIndex(reward => reward.data.jackpot)
        : Random.getRandomWeightedIndex(reachableRewards);
    const reward = reachableRewards[rewardIndex];
    // Reward is scaled with highest completed level multiplied by specific reward multiplier from Plinko reward data.
    const adjustedRewardData = scaleCoinRewardWithLevelCost(
        reward.data,
        LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState))
    );

    state = {
        ...state,
        playerState: InventoryStateHelper.giveReward(state.playerState, adjustedRewardData, { queueTask: false }),
    };

    // Update state coin task with new plinko reward values
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            tasks: state.playerState.tasks.map(task => {
                if (task.task === PlayerTask.COIN) {
                    return {
                        ...task,
                        previousCoins: task.previousCoins + adjustedRewardData.value,
                    };
                }
                return task;
            }),
        },
    };

    trackResourceEvent(state, {
        transactionType: TransactionType.REWARD,
        tActionType: TActionType.PLINKO_REWARD,
    });

    state = {
        ...state,
        plinkoState: {
            ...state.plinkoState,
            dropAnimation: payload.anim,
            startIndex: payload.startIndex,
            rewardIndex: reward.data.index,
            path: getTokenPathing(payload.startIndex, reward.data.index),
        },
        playerState: InventoryStateHelper.setInventory(
            state.playerState,
            RewardType.PLINKO_TOKEN,
            state.playerState.inventory[RewardType.PLINKO_TOKEN] - 1
        ),
    };

    return state;
};

function getTokenPathing(startIndex: number, endIndex: number): PlinkoPathValue[] {
    // TODO: Pass config to state?
    const config = getPlinkoConfig();

    const path: PlinkoPathValue[] = [];
    let currentIndex = startIndex;
    for (let rowIndex = 0; rowIndex < config.board.length - 2; rowIndex++) {
        let direction = Random.sign();
        const newIndex = getNextIndex(direction, currentIndex);
        const maxPossibleRewardIndex = config.board.length - 2 + newIndex - rowIndex;
        const minPossibleRewardIndex = newIndex;

        if (!MathHelper.isWithinRange(endIndex, minPossibleRewardIndex, maxPossibleRewardIndex)) {
            direction = -direction;
        }
        currentIndex = getNextIndex(direction, currentIndex);
        path.push(direction as PlinkoPathValue);
    }
    path.push(currentIndex === endIndex ? -1 : 1);
    return path;

    function getNextIndex(direction: number, index: number): number {
        return direction < 0 ? index : index + 1;
    }
}

export function buyPlinkoToken(state: IGameState, product: IAPReward): IGameState {
    if (getRemainingPurchasablePlinkoTokens(state) > 0) {
        state = ContextStateHelper.setContext(state, ContextType.PURCHASE_PRODUCT, { product });
        state = {
            ...state,
            plinkoState: {
                ...state.plinkoState,
                isRewardClaimable: false,
            },
        };
    }

    return state;
}

export function setPlinkoRewardClaimable(state: IGameState): IGameState {
    return {
        ...state,
        plinkoState: {
            ...state.plinkoState,
            isRewardClaimable: true,
        },
    };
}

export function setRewardAnimationComplete(state: IGameState): IGameState {
    state = {
        ...state,
        plinkoState: {
            ...state.plinkoState,
            isRewardAnimationComplete: true,
        },
    };

    if (!canContinuePlinkoMiniGame(state)) {
        state = leavePlinkoScene(state);
    }

    return state;
}

export function claimPlinkoReward(state: IGameState): IGameState {
    state = {
        ...state,
        plinkoState: {
            ...state.plinkoState,
            rewardIndex: null,
            isRewardClaimable: false,
            isRewardAnimationComplete: false,
        },
    };

    return state;
}

export function leavePlinkoScene(state: IGameState): IGameState {
    // If purchases still available
    if (canContinuePlinkoMiniGame(state)) {
        return UIStateHelper.showPopupFullState(state, PopupID.PLINKO_MINI_GAME_QUIT, {});
    }

    // Cannot leave if reward index is defined i.e. the plinko token is currently dropping.
    if (state.plinkoState.rewardIndex) {
        return state;
    }

    // Quit to world scene
    return GameStateHelper.loadWorld(state);
}

export function canContinuePlinkoMiniGame(state: IGameState): boolean {
    return (
        getRemainingPurchasablePlinkoTokens(state) > 0 ||
        InventoryStateHelper.hasInInventory(state, RewardType.PLINKO_TOKEN)
    );
}

export function getRemainingPurchasablePlinkoTokens(state: IGameState): number {
    return state.plinkoState.purchasableTokensRemaining;
}

export function setRemainingPurchasablePlinkoTokens(state: IGameState, purchasableTokensRemaining: number): IGameState {
    return {
        ...state,
        plinkoState: {
            ...state.plinkoState,
            purchasableTokensRemaining,
        },
    };
}

export function resetPlinkoScene(state: IGameState, options: Partial<IPlinkoState>): IGameState {
    return {
        ...state,
        plinkoState: {
            ...initialPlinkoState,
            ...options,
        },
    };
}

export function isPlinkoRewardClaimable(state: IGameState): boolean {
    return Boolean(state.plinkoState.isRewardClaimable);
}

export function isPlinkoRewardAnimationCompleted(state: IGameState): boolean {
    return state.plinkoState.isRewardAnimationComplete;
}
